import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ChildContainerProps,
  LayoutConfig,
  LayoutContextProps,
  LayoutState,
} from 'types'
import { MOBILE_BREAKPOINT } from 'shared/hooks/use-is-mobile'

export const LayoutContext = createContext<LayoutContextProps | undefined>(
  undefined
)

export const LayoutProvider = ({ children }: ChildContainerProps) => {
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>({
    ripple: false,
    inputStyle: 'outlined',
    menuMode: 'static',
    colorScheme: 'dark',
    theme: 'soho-dark',
    scale: 14,
  })

  const [layoutState, setLayoutState] = useState<LayoutState>({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    profileSidebarVisible: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
  })

  const isOverlay = useCallback(() => {
    return layoutConfig.menuMode === 'overlay'
  }, [layoutConfig.menuMode])

  // Read imperatively at click time (not reactive state), so these stay plain
  // callbacks instead of using the useIsMobile hook.
  const isTinyDesktop = useCallback(() => {
    return window.innerWidth > MOBILE_BREAKPOINT && window.innerWidth < 1500
  }, [])

  const isDesktop = useCallback(() => {
    return window.innerWidth > MOBILE_BREAKPOINT
  }, [])
  const onMenuToggle = useCallback(() => {
    if (isOverlay()) {
      setLayoutState((prevLayoutState) => ({
        ...prevLayoutState,
        overlayMenuActive: !prevLayoutState.overlayMenuActive,
      }))
    }

    if (isDesktop()) {
      setLayoutState((prevLayoutState) => ({
        ...prevLayoutState,
        staticMenuDesktopInactive: !prevLayoutState.staticMenuDesktopInactive,
      }))
    } else {
      setLayoutState((prevLayoutState) => ({
        ...prevLayoutState,
        staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive,
      }))
    }
  }, [isOverlay, isDesktop])

  const showProfileSidebar = useCallback(() => {
    setLayoutState((prevLayoutState) => ({
      ...prevLayoutState,
      profileSidebarVisible: !prevLayoutState.profileSidebarVisible,
    }))
  }, [])

  useEffect(() => {
    console.info('Layout Context initialized 🚗🔥')
  }, [])

  const value = useMemo<LayoutContextProps>(
    () => ({
      layoutConfig,
      setLayoutConfig,
      layoutState,
      setLayoutState,
      onMenuToggle,
      showProfileSidebar,
    }),
    [layoutConfig, layoutState, onMenuToggle, showProfileSidebar]
  )

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  )
}

// Custom hook with error handling
export const useLayout = () => {
  const context = useContext(LayoutContext)
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider')
  }
  return context
}
